import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDebounce } from "use-debounce";
import { motion, AnimatePresence } from "framer-motion";
import { useProductsQuery, usePosProductsQuery } from "../../../Services";
import {
  Search,
  Loader2,
  X,
  Package,
  Barcode,
  Hash,
  CornerDownLeft,
} from "lucide-react";
import { Product } from "../../../Types";
import { useKeyboardShortcut, useScrollIntoView } from "../../../Hook";
import { IMAGE_URL, SHORTCUTS, DEBOUNCED_SEARCH_QUERY } from "../../../Constant";

interface ProductSearchBarProps {
  onSelectProduct: (product: Product) => void;
  selectedProduct?: any;
  handleConfirmAdd?: () => void;
}

export const ProductSearchBar: React.FC<ProductSearchBarProps> = ({
  onSelectProduct,
  selectedProduct,
  handleConfirmAdd,
}) => {
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebounce(query, DEBOUNCED_SEARCH_QUERY);
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const trimmedQuery = debouncedQuery.trim();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastMousePos = useRef({ x: 0, y: 0 });

  const handleMouseMove = (e: React.MouseEvent, index: number) => {
    if (
      e.clientX === lastMousePos.current.x &&
      e.clientY === lastMousePos.current.y
    ) {
      return;
    }
    lastMousePos.current = { x: e.clientX, y: e.clientY };
    setFocusedIndex(index);
  };

  useScrollIntoView(
    focusedIndex,
    ".search-results-container",
    ".search-result-highlighted",
  );

  useKeyboardShortcut(
    SHORTCUTS.POS.SELECT_PRODUCT_FROM_SEARCH,
    (e) => {
      if (!selectedProduct) return;
      e.preventDefault();
      e.stopPropagation();
      handleConfirmAdd?.();
    },
    { enabled: !!selectedProduct },
  );

  const { data: posProducts = [], isLoading: posProductsLoading } = usePosProductsQuery();
  
  const isLocalSearchAvailable = posProducts.length > 0;

  const { data: productsData, isLoading: queryLoading } = useProductsQuery(
    {
      page: 1,
      limit: 10,
      search: trimmedQuery,
      isPos: true,
    },
    { enabled: trimmedQuery.length > 0 && !isLocalSearchAvailable },
  );

  const queryResults = useMemo(() => {
    const activeQuery = isLocalSearchAvailable ? query : trimmedQuery;
    const q = activeQuery.trim().toLowerCase();
    if (!q) return [];
    
    if (isLocalSearchAvailable) {
      return posProducts.filter(p => 
        (p.title && p.title.toLowerCase().includes(q)) ||
        (p.sku_code && p.sku_code.toLowerCase().includes(q)) ||
        (p.barcode && p.barcode.toLowerCase().includes(q))
      ).slice(0, 10);
    } else {
      return productsData?.data?.slice(0, 10) || [];
    }
  }, [posProducts, productsData, query, trimmedQuery, isLocalSearchAvailable]);

  useKeyboardShortcut(SHORTCUTS.POS.FOCUS_SEARCH, () => {
    if (document.activeElement !== inputRef.current) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  });

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const results = useMemo(() => {
    return query.trim() === "" ? [] : queryResults;
  }, [query, queryResults]);

  const isLoading = query.trim() === "" 
    ? false 
    : (isLocalSearchAvailable ? posProductsLoading : queryLoading);

  useEffect(() => {
    const nextIndex = results.length > 0 ? 0 : -1;
    setFocusedIndex((prev) => (prev !== nextIndex ? nextIndex : prev));
  }, [results]);

  const selectItem = (product: Product) => {
    onSelectProduct(product);
    setQuery("");
    setIsOpen(false);
    inputRef.current?.focus();
  };

  useEffect(() => {
    const activeQuery = isLocalSearchAvailable ? query.trim() : trimmedQuery;
    if (activeQuery && queryResults && queryResults.length > 0) {
      const exactMatch = queryResults.find(
        (p) =>
          (p.barcode &&
            p.barcode.toLowerCase() === activeQuery.toLowerCase()) ||
          (p.sku_code &&
            p.sku_code.toLowerCase() === activeQuery.toLowerCase()),
      );

      if (exactMatch) {
        selectItem(exactMatch);
      }
    }
  }, [queryResults, query, trimmedQuery, isLocalSearchAvailable]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter") {
        if (query.trim() !== "") {
          setIsOpen(true);
          e.preventDefault();
          e.stopPropagation();
        }
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        e.stopPropagation();
        if (results.length > 0) {
          setFocusedIndex((prev) => (prev + 1) % results.length);
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        e.stopPropagation();
        if (results.length > 0) {
          setFocusedIndex(
            (prev) => (prev - 1 + results.length) % results.length,
          );
        }
        break;
      case "Enter":
        e.preventDefault();
        e.stopPropagation();
        if (focusedIndex >= 0 && focusedIndex < results.length) {
          selectItem(results[focusedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(false);
        inputRef.current?.focus();
        break;
      default:
        break;
    }
  };

  const clearSearch = () => {
    setQuery("");
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const getProductImage = (product: Product) => {
    try {
      const imgs = JSON.parse(product.image_paths || "[]");
      if (imgs.length > 0) {
        return `${IMAGE_URL}/${imgs[0]}`;
      }
    } catch {
      // JSON parse error or empty array
    }
    return null;
  };

  const getProductStock = (product: Product) => {
    return product.total_quantity || 0;
  };

  const getProductPrice = (product: Product) => {
    if (product.stocks && product.stocks.length > 0) {
      const defaultStock =
        product.stocks.find((s) => s.is_default === 1) || product.stocks[0];
      return defaultStock.retail_price;
    }
    return product.mrp;
  };

  return (
    <div ref={containerRef} className="relative w-full z-30">
      <div className="relative group">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center text-gray-400 group-focus-within:text-indigo-600 transition-colors">
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Search className="w-5 h-5" />
          )}
        </div>

        <input
          ref={inputRef}
          id="pos-search-input"
          autoFocus
          type="text"
          placeholder="Search by Product Name (fuzzy), SKU, Barcode or ID..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsOpen(true)}
          className="w-full pl-12 pr-20 py-3 bg-white rounded-xl border border-gray-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm text-gray-800 placeholder-gray-400 font-medium"
        />

        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {query ? (
            <button
              type="button"
              onClick={clearSearch}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          ) : (
            <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 border border-gray-200 rounded bg-gray-50/80 shadow-xs font-sans text-[10px] font-extrabold text-gray-400 select-none cursor-default transition-all duration-300 group-focus-within:border-indigo-200 group-focus-within:text-indigo-500">
              {SHORTCUTS.POS.FOCUS_SEARCH.key}
            </kbd>
          )}
        </div>
      </div>

      <AnimatePresence>
        {isOpen && query.trim() !== "" && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 4, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="absolute left-0 right-0 overflow-hidden bg-white/95 backdrop-blur-md border border-gray-200/80 rounded-2xl shadow-xl shadow-gray-200/50 mt-1 max-h-[500px] flex flex-col z-40"
          >
            <div className="px-4 py-2 bg-gray-50/80 border-b border-gray-100 flex items-center justify-between text-[11px] text-gray-400 font-semibold select-none">
              <span>SEARCH RESULTS</span>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <span className="border border-gray-200 px-1 py-0.5 rounded bg-white shadow-xs font-mono">
                    ↑↓
                  </span>{" "}
                  Navigate
                </span>
                <span className="flex items-center gap-1">
                  <span className="border border-gray-200 px-1 py-0.5 rounded bg-white shadow-xs font-mono">
                    ↵
                  </span>{" "}
                  Select
                </span>
                <span className="flex items-center gap-1">
                  <span className="border border-gray-200 px-1 py-0.5 rounded bg-white shadow-xs font-mono">
                    ESC
                  </span>{" "}
                  Close
                </span>
              </div>
            </div>

            <div className="overflow-y-auto custom-scrollbar flex-1 max-h-[440px] p-2 flex flex-col gap-1 search-results-container">
              {results.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 px-4 text-center select-none">
                  <Package className="w-10 h-10 text-gray-300 mb-2 stroke-[1.5]" />
                  <p className="text-gray-500 font-semibold text-sm">
                    No products found
                  </p>
                  <p className="text-gray-400 text-xs mt-1 max-w-[240px]">
                    No active product matches "{query}". Double check your
                    spelling or SKU code.
                  </p>
                </div>
              ) : (
                results.map((product, index) => {
                  const image = getProductImage(product);
                  const stock = getProductStock(product);
                  const price = getProductPrice(product);
                  const isFocused = index === focusedIndex;

                  return (
                    <div
                      key={product.id}
                      onClick={() => selectItem(product)}
                      onMouseMove={(e) => handleMouseMove(e, index)}
                      className={`flex items-center gap-3 p-2.5 rounded-xl cursor-pointer transition-all border ${
                        isFocused
                          ? "bg-indigo-50/80 border-indigo-200 shadow-sm search-result-highlighted"
                          : "border-transparent hover:bg-gray-50/80"
                      }`}
                    >
                      <div className="w-12 h-12 rounded-lg bg-gray-100 border border-gray-200/50 flex-shrink-0 flex items-center justify-center overflow-hidden">
                        {image ? (
                          <img
                            src={image}
                            alt={product.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Package className="w-6 h-6 text-gray-400 stroke-[1.5]" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0 flex flex-col">
                        <div className="flex items-center justify-between gap-2">
                          <h4
                            className={`text-sm font-semibold truncate ${
                              isFocused ? "text-indigo-900" : "text-gray-800"
                            }`}
                          >
                            {product.title}
                          </h4>
                          <span className="font-extrabold text-sm text-green-600 bg-green-50 px-2 py-0.5 rounded-lg whitespace-nowrap">
                            LKR{" "}
                            {price.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </div>

                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                          <span className="flex items-center gap-1 text-[10px] text-gray-500 bg-gray-100/80 px-1.5 py-0.5 rounded-md">
                            <Hash className="w-3 h-3 text-gray-400" />
                            ID:{" "}
                            <span className="font-semibold text-gray-700">
                              {product.id}
                            </span>
                          </span>

                          <span className="flex items-center gap-1 text-[10px] text-gray-500 bg-gray-100/80 px-1.5 py-0.5 rounded-md">
                            <Package className="w-3 h-3 text-gray-400" />
                            SKU:{" "}
                            <span className="font-semibold text-gray-700">
                              {product.sku_code}
                            </span>
                          </span>

                          <span className="flex items-center gap-1 text-[10px] text-gray-500 bg-gray-100/80 px-1.5 py-0.5 rounded-md">
                            <Barcode className="w-3 h-3 text-gray-400" />
                            Code:{" "}
                            <span className="font-semibold text-gray-700">
                              {product.barcode}
                            </span>
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-col items-end justify-center gap-1 text-right flex-shrink-0 min-w-[70px]">
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            stock <= 0
                              ? "bg-red-50 text-red-500"
                              : stock <= product.alert_quantity
                                ? "bg-amber-50 text-amber-500"
                                : "bg-emerald-50 text-emerald-600"
                          }`}
                        >
                          {stock <= 0
                            ? "Out of stock"
                            : `${stock} ${product.unit}`}
                        </span>

                        {isFocused && (
                          <span className="flex items-center gap-0.5 text-[9px] font-semibold text-indigo-400 animate-pulse mt-0.5">
                            Add <CornerDownLeft className="w-2.5 h-2.5" />
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
